// EngineReview.tsx — session 4 K2: the customer's review of a video made by the engine (videos.run_id set). One
// component, one read (GET /videos/:id/engine — the workers' filmstrip, customer view), three stages by the run's OPEN
// GATE, never by a status the page guesses:
//   gate_script     → the script, beat by beat: edit a beat's words (the run re-plans from them), or approve;
//   gate_storyboard → the storyboard: every beat's frame + the animatic; approve;
//   finished        → the video; download = accepting it (the run's final decision).
// Anything else is "in progress" with a refresh. Every action is a gate decision on the backend.
import { CompareExecutions } from "./CompareExecutions";
import { useFeature } from "@/api/features.ts";
import { creditsLine, progressFlags, headlineFor, finishedVideoShown, type UserStateView, type CreditOutcome } from "./engine-wording.ts";
import { PostNumbers } from "./PostNumbers.tsx";   // (tf3-35a) beside the curve
import { RetentionCurves } from "../../pages/admin/RetentionCurves.tsx";   // (tf3-34b) the operators' chart, for the owner too
/** (tf3-34b) said until a platform reports how long viewers stayed */
import { useEffect, useRef, useState } from "react";
import { STAGES, stageOf, progressPct, type RunProgress } from "./engine-progress.ts";
import { CONNECT_MESSAGE, CONNECT_RESULT_KEY, connectErrorText, type ConnectResult } from "@/lib/connect-popup.ts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { videosApi, publishingApi, FINISHING_LABEL, type EngineBeat } from "@/api/videos.ts";
import { scriptsApi } from "@/api/scripts.ts";
import { storyboardsApi } from "@/api/storyboards.ts";
import { TfCard, Btn, TfSpinner } from "@/components/v2/primitives.tsx";
import { TikTokPostForm } from "./TikTokPostForm.tsx";
import { partlyPosted, partlySplit, selectionKey, skipText } from "@/lib/post-result.ts";
import { NeedsYouCard } from "./NeedsYouCard.tsx";
import type { NeedsInputView } from "@/lib/needs-input.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { WhyStopped } from "./GateNotes.tsx";   // (TF-6b-4) "Why we stopped here"
import { DryRunStopped } from "./DryRun.tsx";   // (tf3-35b)
import { isDryRunStopped } from "@/lib/dry-run.ts";
import { w, useWords } from "@/lib/words.ts";   // (B107) the page's own words, editable in Admin → Words
import { MaterialChanges } from "./MaterialChanges.tsx";   // (B112) what changed from the plan, above Approve at the final review

export function EngineReview({ videoId }: { videoId: string }) {
  useWords();   // (B107) the store's words, read once; the page re-renders when they arrive
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["engine", videoId], queryFn: () => videosApi.engine(videoId), refetchInterval: 10_000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ["engine", videoId] });
  // (TF-6b-1) an admin sees a question's facts labelled; a customer the card's line (never the engine's reason)
  const role = useAuthStore((st) => st.user?.role);
  const isAdmin = role === "admin" || role === "superadmin";
  const compareOn = useFeature("compare_openings");   // session 7 3.13: beta — operators only
  const approveScript = useMutation({ mutationFn: () => scriptsApi.approve(videoId), onSuccess: refresh });
  const approveStoryboard = useMutation({ mutationFn: () => storyboardsApi.approve(videoId), onSuccess: refresh });
  const approveFinal = useMutation({ mutationFn: () => videosApi.approveFinal(videoId) });
  // Session 6 W1b: an agency-post video's file is its finished CUT, not the render: the download (which approves
  // delivery) and the preview must be that file. Engine-tail videos keep the render (it is their final file).
  // A hook, so above every early return.
  const fin = useQuery({ queryKey: ["video-finishing", videoId], queryFn: () => publishingApi.finishing(videoId),
    enabled: !!q.data?.filmstrip?.document?.render?.url });
  const player = useRef<HTMLVideoElement>(null);   // (B112) a change's clip moves it to that moment

  if (q.isLoading) return <TfSpinner />;
  if (q.isError || !q.data?.filmstrip) return <TfCard padding={24}>{w("page.video.not_loaded")} <Btn variant="ghost" onClick={refresh}>{w("page.video.retry")}</Btn></TfCard>;
  const fs = q.data.filmstrip;
  const beats: EngineBeat[] = fs.segments.flatMap((s) => s.beats).sort((a, b) => a.position - b.position);
  const gate = fs.run?.awaitingGate?.stepId ?? null;
  const renderUrl = fs.document?.render?.url ?? null;
  const fileUrl = fin.data?.finishing?.cutUrl ?? renderUrl;
  const err = (approveScript.error ?? approveStoryboard.error) as { message?: string } | null;

  // Session 4 L3: the proposals — the candidates side by side, as their preview stills; choosing one is the
  // gate_variant decision, and the run continues on THAT candidate (its script and choices become variant_choice).
  if (gate === "gate_variant") return <Proposals videoId={videoId} onChosen={refresh} />;
  // Session 6 T3: the executions of the approved storyboard, side by side — pick the opening to make
  if (gate === "gate_compare") return compareOn ? <CustomerCompare videoId={videoId} onDone={refresh} />
    : <TfCard padding={24}><h2 style={{ marginTop: 0 }}>{w("page.opening.choosing_title")}</h2><p className="tf-muted">{w("page.opening.choosing_body")}</p></TfCard>;

  if (gate === "gate_script") return (
    <TfCard padding={24}>
      <h2 style={{ marginTop: 0 }}>{w("page.script.title")}</h2>
      <p className="tf-muted">{w("page.script.body")}</p>
      <WhyStopped videoId={videoId} />
      {beats.map((b) => <BeatWords key={b.beatId} videoId={videoId} beat={b} onSaved={refresh} />)}
      {err && <p style={{ color: "var(--tf-danger, #c00)" }}>{err.message}</p>}
      <Btn onClick={() => approveScript.mutate()} disabled={approveScript.isPending}>{w("page.script.approve")}</Btn>
    </TfCard>
  );

  if (gate === "gate_storyboard") return (
    <TfCard padding={24}>
      <h2 style={{ marginTop: 0 }}>{w("page.storyboard.title")}</h2>
      <WhyStopped videoId={videoId} />
      {fs.document?.animaticUrl && <video src={fs.document.animaticUrl} controls style={{ width: "100%", maxWidth: 480, borderRadius: 8 }} />}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, margin: "16px 0" }}>
        {beats.map((b) => {
          const src = b.previews?.frame?.thumbUrl ?? b.production?.image?.url ?? null;
          return <figure key={b.beatId} style={{ margin: 0 }}>
            {src ? <img src={src} alt={b.name ?? b.beatId} style={{ width: "100%", borderRadius: 6 }} /> : <div className="tf-muted">{w("page.storyboard.no_frame")}</div>}
            <figcaption className="tf-muted" style={{ fontSize: 11 }}>{b.spokenText ?? ""}</figcaption>
          </figure>;
        })}
      </div>
      {err && <p style={{ color: "var(--tf-danger, #c00)" }}>{err.message}</p>}
      <Btn onClick={() => approveStoryboard.mutate()} disabled={approveStoryboard.isPending}>{w("page.storyboard.approve")}</Btn>
    </TfCard>
  );

  // (creative-1 C1.5, review J1) a rendered video the final screening holds (or hasn't passed yet) isn't the customer's
  //  to watch, download or post: the progress page says "We're checking your finished video" instead
  if (renderUrl && finishedVideoShown(q.data.status, (q.data as { userState?: { basis?: string } | null }).userState ?? null)) return (
    <TfCard padding={24}>
      <h2 style={{ marginTop: 0 }}>{w("page.final.title")}</h2>
      <video ref={player} src={fileUrl ?? undefined} controls style={{ width: "100%", maxWidth: 480, borderRadius: 8 }} />
      {/* (tf3-34b) how long viewers stayed, once a platform has reported it (YouTube; TikTok reports none) */}
      {q.data.status === "published" && <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
        <RetentionCurves videoId={videoId} emptyText={w("page.retention.not_yet")} /><PostNumbers videoId={videoId} /></div>}
      {gate === "gate_final" ? <><MaterialChanges videoId={videoId} beats={beats} player={player} /><Delivery videoId={videoId} onDone={refresh} videoUrl={fileUrl} /></>
        : <>
            <p className="tf-muted">{q.data.status === "published" ? w("page.final.published") : q.data.status === "publishing" ? w("page.final.posting") : ""}</p>
            {/* Session 4 L1: delivered — post it (again, or to a channel connected since): the same video, no new render. */}
            {/* Session 6 W1b: a finishing run parked at gate_delivery is the customer's to decide — their Post tap IS that decision. */}
            {(!gate || gate === "gate_delivery") && q.data.status !== "publishing" && <Delivery videoId={videoId} onDone={refresh} later videoUrl={fileUrl} />}
          </>}
      <div style={{ marginTop: 12 }}>
        {/* Downloading accepts the video WITHOUT posting it — the run's final decision (idempotent). */}
        <a href={fileUrl ?? undefined} download onClick={() => approveFinal.mutate()}><Btn variant="ghost">{w("page.final.download_only")}</Btn></a>
      </div>
    </TfCard>
  );

  // Session 7 3.6 / N5: the video waits on the customer — the specialist's list, and (when offered) the topic-led angle
  // (TF-6b-1) every question the engine asks: the card answers it with the questionId it shows (lib/needs-input.ts)
  const ni = (q.data as { needsInput?: NeedsInputView | null }).needsInput ?? null;
  // (tf3-35a) the account's credits pay a retake here: its cost in credits (null: not priced yet)
  if (ni) return <NeedsYouCard ni={ni} admin={isAdmin} retakeCredits={(q.data as { retakeCredits?: number | null }).retakeCredits ?? null} answer={(choice, questionId) => videosApi.answerNeedsInput(videoId, choice, questionId)}
    onAnswered={refresh} onChanged={refresh} />;

  const progress = <EngineProgress progress={fs.run?.progress ?? null} runStatus={fs.run?.status ?? q.data.status ?? null}
    userState={(q.data as { userState?: UserStateView | null }).userState ?? null}
    credits={(q.data as { credits?: CreditOutcome | null }).credits ?? null} />;
  // (tf3-35b) a dry run that stopped before its first clip: said, with its cancel (admins), above the progress
  const runId = (q.data as { runId?: string }).runId;
  if (runId && isDryRunStopped({ userState: (q.data as { userState?: UserStateView | null }).userState ?? null }))
    return <><DryRunStopped runId={runId} onCancelled={refresh} />{progress}</>;
  return progress;
}

/** While the engine works: which stage it's in, what it's doing right now, a REAL progress bar (done / total steps),
 *  and the time so far — instead of a bare "Working on it". Updates with the page's own refresh. */
/** Session 7 N10: the run's situation for the user (contracts userStateOf, served by the backend from the run summary). */
// (session 7 §7e: the words are pure, in engine-wording.ts, under `npm test`)
export { creditsLine };

function EngineProgress({ progress, runStatus, userState, credits }: { progress: RunProgress | null; runStatus: string | null;
    userState: UserStateView | null; credits: CreditOutcome | null }) {
  const [since] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  // (never backwards: an unrecognised step keeps the stage last shown — a run does not un-plan itself)
  const lastStage = useRef(0);
  const cur = stageOf(progress?.currentStepId ?? null, lastStage.current);
  if (cur > lastStage.current) lastStage.current = cur;
  const pct = progressPct(progress);
  // (session 7: the engine's user state decides the words; nothing animates unless the system is actually working)
  const { failed, working } = progressFlags(userState, runStatus);
  const secs = Math.floor((now - since) / 1000);
  return (
    <TfCard padding={24}>
      <style>{`@keyframes ep-stripes { from { background-position: 0 0 } to { background-position: 40px 0 } }`}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>{headlineFor(userState, runStatus)}</h2>
        <span className="tf-muted" style={{ fontSize: 12 }}>{secs < 60 ? w("page.progress.on_this_page.sec", { s: secs }) : w("page.progress.on_this_page.min", { m: Math.floor(secs / 60), s: String(secs % 60).padStart(2, "0") })}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: "var(--tf-rule, #e5e7eb)", overflow: "hidden", margin: "14px 0" }}>
        {pct != null
          ? <div style={{ height: "100%", width: `${pct}%`, background: failed ? "#dc2626" : "var(--tf-accent, #4f46e5)", transition: "width .4s" }} />
          : <div style={{ height: "100%", animation: working ? "ep-stripes .8s linear infinite" : "none", background: "repeating-linear-gradient(45deg, var(--tf-accent, #4f46e5) 0 10px, #a5b4fc 10px 20px)", backgroundSize: "40px 100%" }} />}
      </div>
      <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
        {STAGES.map((s, i) => {
          const st = i < cur ? "done" : i === cur ? (failed ? "failed" : "now") : "todo";
          return <li key={s.id} style={{ display: "flex", gap: 8, fontSize: 14, color: st === "todo" ? "var(--tf-muted, #9ca3af)" : "inherit" }}>
            <span style={{ width: 16, color: st === "done" ? "#16a34a" : st === "failed" ? "#dc2626" : st === "now" ? "var(--tf-accent, #4f46e5)" : "#9ca3af" }}>{st === "done" ? "✓" : st === "failed" ? "✗" : st === "now" ? "●" : "○"}</span>
            <span style={{ fontWeight: st === "now" ? 600 : 400 }}>{s.label}{s.you ? ` ${w("page.progress.you")}` : ""}</span>
          </li>;
        })}
      </ol>
      <p className="tf-muted" style={{ marginTop: 14, marginBottom: 0 }}>
        {failed ? [userState?.message ?? `${w("page.progress.could_not_be_made")}.`, creditsLine(credits)].filter(Boolean).join(" ")
          : userState && userState.situation !== "working" ? userState.message
          : pct != null ? w("page.progress.now_counted", { stage: STAGES[cur]!.now, done: progress!.done, total: progress!.total }) : w("page.progress.now", { stage: STAGES[cur]!.now })}
      </p>
    </TfCard>
  );
}

function BeatWords({ videoId, beat, onSaved }: { videoId: string; beat: EngineBeat; onSaved: () => void }) {
  const [text, setText] = useState(beat.spokenText ?? "");
  const [editing, setEditing] = useState(false);
  // (session 7 3.13: editing a beat's words mid-run is BETA — operators only until a real run certifies it)
  const canEdit = useFeature("script_edit_mid_run");
  const save = useMutation({ mutationFn: () => videosApi.editBeatWords(videoId, beat.beatId, text.trim()),
    onSuccess: () => { setEditing(false); onSaved(); } });
  return (
    <div style={{ borderTop: "1px solid var(--tf-rule)", padding: "10px 0" }}>
      <div className="tf-muted" style={{ fontSize: 11 }}>{beat.name ?? w("page.beat.name", { n: beat.position })}</div>
      {editing ? (
        <>
          <textarea className="tf-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} />
          <Btn onClick={() => save.mutate()} disabled={save.isPending || !text.trim()}>{w("page.beat.save")}</Btn>{" "}
          <Btn variant="ghost" onClick={() => { setEditing(false); setText(beat.spokenText ?? ""); }}>{w("page.beat.cancel")}</Btn>
          {save.error && <p style={{ color: "var(--tf-danger, #c00)" }}>{(save.error as { message?: string }).message}</p>}
        </>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <p style={{ margin: 0, flex: 1 }}>{beat.spokenText ?? <span className="tf-muted">{w("page.beat.no_words")}</span>}</p>
          {canEdit && <Btn variant="ghost" onClick={() => setEditing(true)}>{w("page.beat.edit")}</Btn>}
        </div>
      )}
    </div>
  );
}

/** Session 4 L3 — the customer's proposals screen: the candidates as preview stills, side by side. */
function Proposals({ videoId, onChosen }: { videoId: string; onChosen: () => void }) {
  const q = useQuery({ queryKey: ["proposals", videoId], queryFn: () => publishingApi.proposals(videoId) });
  const choose = useMutation({ mutationFn: (id: string) => publishingApi.choose(videoId, id), onSuccess: onChosen });
  if (q.isLoading) return <TfSpinner />;
  const cands = q.data?.candidates ?? [];
  return (
    <TfCard padding={24}>
      <h2 style={{ marginTop: 0 }}>{w("page.direction.title")}</h2>
      <p className="tf-muted">{w("page.direction.body")}</p>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(cands.length, 3) || 1}, minmax(0, 1fr))`, gap: 16 }}>
        {cands.map((c) => (
          <div key={c.candidateId} style={{ border: "1px solid var(--tf-rule)", borderRadius: 8, padding: 12 }}>
            {c.previewUrl ? <img src={c.previewUrl} alt={c.angle ?? c.candidateId} style={{ width: "100%", borderRadius: 6 }} /> : <div className="tf-muted">{w("page.direction.no_preview")}</div>}
            {c.angle && <div style={{ fontWeight: 600, marginTop: 8 }}>{c.angle}</div>}
            <p className="tf-muted" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{c.script.slice(0, 280)}{c.script.length > 280 ? "…" : ""}</p>
            <Btn onClick={() => choose.mutate(c.candidateId)} disabled={choose.isPending}>{w("page.direction.choose")}</Btn>
          </div>
        ))}
      </div>
      {choose.error && <p style={{ color: "var(--tf-danger, #c00)" }}>{(choose.error as { message?: string }).message}</p>}
    </TfCard>
  );
}

/** (tf3-27) how many posts a Post made (one per distinct file); a Publish answers none. */
const postCount = (d: unknown): number => { const ids = (d as { postIds?: unknown } | undefined)?.postIds; return Array.isArray(ids) ? ids.length : 0; };

/** Session 4 L1 — delivery: the customer's connected channels (default: all selected), connect another, publish. */
function Delivery({ videoId, onDone, later = false, videoUrl = null }: { videoId: string; onDone: () => void; later?: boolean; videoUrl?: string | null }) {
  const qc = useQueryClient();
  const ch = useQuery({ queryKey: ["publishing-channels"], queryFn: () => publishingApi.channels() });
  // (tf3-26) TikTok is posted only from its own form (TikTok's Content Sharing Guidelines): never ticked with the others
  const connected = (ch.data?.channels ?? []).filter((c) => c.status === "active");
  const tiktok = connected.filter((c) => c.channel === "tiktok");
  const active = connected.filter((c) => c.channel !== "tiktok");
  // (audit 10-15) each account's form opens on its own: opening a second never closes one already posting
  const [tiktokOpen, setTiktokOpen] = useState<Set<string>>(new Set());
  // @vms/publishing 0.3: a channel lacking a required scope (e.g. after a scope was added) — a reconnect card, not a tick box.
  const reconnect = (ch.data?.channels ?? []).filter((c) => c.status === "needs_reconnect");
  const [off, setOff] = useState<Set<string>>(new Set());
  // Session 6 W2: "Schedule post" — an optional time (the browser's local time); empty posts now.
  const [at, setAt] = useState("");
  // Session 6 W1a.1: a Post goes out only from the CURRENT cut. When a newer finishing run is in progress (or the video
  // is still being finished / awaiting approval), say so here and hold the button, with the earlier cuts as history —
  // the rule, shown, rather than a Post that fails. The engine refuses the same Post with the same reason regardless.
  const fin = useQuery({ queryKey: ["video-finishing", videoId], queryFn: () => publishingApi.finishing(videoId), enabled: later });
  const finishing = later ? fin.data?.finishing ?? null : null;
  const heldByFinishing = !!finishing && !finishing.postable;
  // (tf3-28) a Post that went out part-way holds the button for that same selection (its posts went out already)
  const chosenKey = selectionKey(active.filter((c) => !off.has(c.channelId)).map((c) => c.channelId));
  const [partlyFor, setPartlyFor] = useState<string | null>(null);
  // (audit 10-16) the selection is fixed when Post is pressed (the mutate variable): a tick changed while the Post is in
  //  flight never moves the hold to another selection
  const publish = useMutation({
    mutationFn: (_sent: { key: string }) => {
      const chosen = active.filter((c) => !off.has(c.channelId)).map((c) => ({ channelId: c.channelId, channel: c.channel }));
      // At delivery: the gate_final decision. After it: a Post of the same final video (L1 publish-later).
      return later ? publishingApi.postLater(videoId, chosen, at ? new Date(at).toISOString() : undefined) : publishingApi.publishRunBacked(videoId, chosen);
    },
    onSuccess: (d, sent) => { setPartlyFor(partlyPosted(d) ? sent.key : null); onDone(); },
  });
  const partly = partlyPosted(publish.data);
  const [connectError, setConnectError] = useState<string | null>(null);
  // The popup reports its result (lib/connect-popup.ts) — refresh the list, show a refusal.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.data?.type !== CONNECT_MESSAGE) return;
      const r = e.data.result as ConnectResult;
      setConnectError(r.error ? connectErrorText(r.error) : null);
      void qc.invalidateQueries({ queryKey: ["publishing-channels"] });
    };
    window.addEventListener("message", onMsg);
    // A connect finished in THIS window (no popup): its result was kept for the page.
    try {
      const kept = sessionStorage.getItem(CONNECT_RESULT_KEY);
      if (kept) { sessionStorage.removeItem(CONNECT_RESULT_KEY); const r = JSON.parse(kept) as ConnectResult; setConnectError(r.error ? connectErrorText(r.error) : null); }
    } catch { /* storage off */ }
    return () => window.removeEventListener("message", onMsg);
  }, [qc]);
  const connect = async (channel: string) => {
    setConnectError(null);
    // Opened synchronously, inside the click — a window opened after an await is blocked by popup blockers.
    const popup = window.open("about:blank", "tf-connect", "width=520,height=720");
    try {
      // The callback redirects the popup back to this page, which reports and closes (lib/connect-popup.ts).
      const { authorizeUrl } = await publishingApi.connect(channel, window.location.href);
      if (popup) popup.location.href = authorizeUrl; else window.location.href = authorizeUrl;
    } catch (e) {
      popup?.close();
      setConnectError((e as { message?: string }).message ?? w("page.post.connect_not_started"));
      return;
    }
    // Belt and braces: the list is also polled until the popup closes.
    const t = window.setInterval(async () => {
      await qc.invalidateQueries({ queryKey: ["publishing-channels"] });
      if (!popup || popup.closed) window.clearInterval(t);
    }, 2000);
  };
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: "0 0 8px" }}>{later ? w("page.post.title_later") : w("page.post.title")}</h3>
      {heldByFinishing && (
        <div style={{ border: "1px solid var(--tf-rule)", borderRadius: 8, padding: 8, margin: "0 0 8px" }}>
          <p style={{ margin: 0 }}><strong>{FINISHING_LABEL[finishing!.state]}</strong></p>
          {finishing!.reason && <p className="tf-muted" style={{ margin: "4px 0 0" }}>{finishing!.reason}</p>}
          {finishing!.history.map((h, i) => (
            <p key={i} className="tf-muted" style={{ margin: "4px 0 0" }}>
              {w("page.post.earlier_cut", { date: new Date(h.createdAt).toLocaleDateString(), state: h.delivery === "approved" ? w("page.post.earlier_cut.approved") : h.status })}
            </p>
          ))}
        </div>
      )}
      {connected.length === 0 && <p className="tf-muted">{w("page.post.none_connected")}</p>}
      {reconnect.map((c) => (
        <div key={c.channelId} style={{ border: "1px dashed var(--tf-rule)", borderRadius: 8, padding: 8, margin: "4px 0", display: "flex", gap: 8, alignItems: "center" }}>
          <span>{c.accountName} <span className="tf-muted">({c.channel}) — {c.statusReason ?? w("page.post.needs_reconnecting")}</span></span>
          <Btn variant="ghost" onClick={() => connect(c.channel)}>{w("page.post.reconnect")}</Btn>
        </div>
      ))}
      {active.map((c) => (
        <label key={c.channelId} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
          <input type="checkbox" checked={!off.has(c.channelId)}
            onChange={(e) => setOff((prev) => { const n = new Set(prev); e.target.checked ? n.delete(c.channelId) : n.add(c.channelId); return n; })} />
          {c.accountName} <span className="tf-muted">({c.channel})</span>
        </label>
      ))}
      {/* (tf3-26) each TikTok account: its own form, opened by the creator; nothing is read from TikTok before that */}
      {tiktok.map((c) => !later ? (
        <p key={c.channelId} className="tf-muted">{w("page.post.tiktok_own_form", { account: c.accountName })}</p>
      ) : tiktokOpen.has(c.channelId) ? (
        <TikTokPostForm key={c.channelId} videoId={videoId} channel={c} videoUrl={videoUrl} initialTitle="" onPosted={onDone}
          held={heldByFinishing ? finishing!.reason ?? FINISHING_LABEL[finishing!.state] : null} />
      ) : (
        <div key={c.channelId} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
          <span>{c.accountName} <span className="tf-muted">(tiktok)</span></span>
          <Btn variant="ghost" onClick={() => setTiktokOpen((prev) => new Set(prev).add(c.channelId))}>{w("page.post.to_tiktok")}</Btn>
        </div>
      ))}
      {later && active.length > 0 && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
          <span className="tf-muted">{w("page.post.at")}</span>
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
          <span className="tf-muted" style={{ fontSize: 12 }}>{at ? "" : w("page.post.at_empty")}</span>
        </label>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {(active.length > 0 || !later) && <Btn onClick={() => publish.mutate({ key: chosenKey })} disabled={publish.isPending || heldByFinishing || active.every((c) => off.has(c.channelId)) || partlyFor === chosenKey}>{later ? (at ? w("page.post.schedule") : w("page.post.post")) : w("page.post.publish")}</Btn>}
        <Btn variant="ghost" onClick={() => connect("tiktok")}>{w("page.post.connect_tiktok")}</Btn>
        <Btn variant="ghost" onClick={() => connect("youtube")}>{w("page.post.connect_youtube")}</Btn>
      </div>
      {publish.error && <p style={{ color: "var(--tf-danger, #c00)" }}>{(publish.error as { message?: string }).message}</p>}
      {/* (tf3-33) which of the channels SENT got their post, by name; with no targets, the backend's message */}
      {partly && (() => {
        const sentIds = (publish.variables?.key ?? "").split(",").filter(Boolean);
        const nameOf = (id: string) => { const c = connected.find((x) => x.channelId === id); return c ? `${c.accountName} (${c.channel})` : id; };
        const split = partlySplit(partly, sentIds, nameOf);
        return !split ? <p role="alert" style={{ color: "var(--tf-danger, #c00)" }}>{partly.message}</p> : (
          <div role="alert" style={{ color: "var(--tf-danger, #c00)" }}>
            <p><strong>{w("page.post.went_out")}</strong> {split.went.join(", ") || "—"}</p>
            <p><strong>{w("page.post.didnt_go_out")}</strong> {split.didnt.join(", ") || "—"} — {partly.reason}</p>
            <p>{w("page.post.dont_repost")}</p>
          </div>);
      })()}
      {later && ((publish.data as { skipped?: { channel: string; reason: string }[] } | undefined)?.skipped ?? []).map((k, i) =>
        <p key={i} className="tf-muted">{skipText(k.reason) === k.reason ? `${k.channel}: ${k.reason}` : skipText(k.reason)}</p>)}
      {/* (tf3-27) one post per distinct file: each platform got its own version */}
      {later && postCount(publish.data) > 1 &&
        <p className="tf-muted">{w("page.post.sent_as", { n: postCount(publish.data) })}</p>}
      {connectError && <p style={{ color: "var(--tf-danger, #c00)" }}>{connectError}</p>}
    </div>
  );
}


function CustomerCompare({ videoId, onDone }: { videoId: string; onDone: () => void }) {
  const q = useQuery({ queryKey: ["compare", videoId], queryFn: () => publishingApi.compare(videoId), refetchInterval: 8000 });
  const choose = useMutation({ mutationFn: (k: number) => publishingApi.compareChoose(videoId, k), onSuccess: onDone });
  const reject = useMutation({ mutationFn: () => publishingApi.compareReject(videoId), onSuccess: () => q.refetch() });
  if (q.isLoading || !q.data) return <TfSpinner />;
  const err = (choose.error ?? reject.error) as { message?: string } | null;
  return (
    <TfCard padding={24}>
      <h2 style={{ marginTop: 0 }}>{w("page.compare.title")}</h2>
      <p className="tf-muted">{w("page.compare.body")}</p>
      <CompareExecutions data={q.data} operator={false} busy={choose.isPending || reject.isPending}
        onChoose={(k) => choose.mutate(k)} onReject={() => reject.mutate()} />
      {err && <p style={{ color: "var(--tf-danger, #c00)" }}>{err.message}</p>}
    </TfCard>
  );
}

